import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ListFilter, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useLocation, useRoute } from "wouter";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";
import DiscountEditor from "@/components/pos/admin/discount-editor";

const fetchDiscounts = async () => {
  const res = await posApiFetch("/v1/pos/discounts");
  if (!res.ok) throw new Error("Failed to fetch discounts");
  return res.json();
};

type DiscountsState = {
  status?: string;
  activationType?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
};

const defaultDiscountsState: DiscountsState = {
  status: "all",
  activationType: "all",
  currentPage: 1,
  itemsPerPage: 10,
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export default function DiscountsPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [isAddRoute] = useRoute("/admin/discounts/add");
  const [isEditRoute, editParams] = useRoute("/admin/discounts/:id/edit");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => { document.title = "Discounts | SECH Admin"; }, []);

  const [discountsState, setDiscountsState] = usePagePersistentState<DiscountsState>("discounts", defaultDiscountsState);
  const searchQuery = discountsState.searchQuery;
  const currentPage = discountsState.currentPage;
  const itemsPerPage = discountsState.itemsPerPage;
  const statusFilter = discountsState.status;
  const activationTypeFilter = discountsState.activationType;
  const dateRange = discountsState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "discounts_table",
    defaultColumn: "name",
    defaultDirection: "asc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "", type: "", dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter ?? "",
      type: activationTypeFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [statusFilter, activationTypeFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setDiscountsState((prev) => ({
      ...prev,
      status: values.status || "all",
      activationType: values.type || "all",
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: discounts = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/discounts"],
    queryFn: fetchDiscounts,
    retry: 3,
    retryDelay: 1000,
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await posApiFetch(`/v1/pos/discounts/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete discount");
      toast({ title: "Discount deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/discounts"] });
    } catch {
      toast({ title: "Error", description: "Failed to delete discount", variant: "destructive" });
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const getFilterCount = () => {
    let count = 0;
    if (discountsState.status && discountsState.status !== "all") count += 1;
    if (discountsState.activationType && discountsState.activationType !== "all") count += 1;
    const fromDate = normalizeDate(discountsState.dateRange?.from);
    const toDate = normalizeDate(discountsState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const typeOptions = [
    { label: "All", value: "all" },
    { label: "Percentage", value: "percentage" },
    { label: "Flat", value: "flat" },
  ];

  const discountFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Option" },
    { key: "type", label: "Discount Type", type: "select", options: typeOptions, placeholder: "Select Option" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "all", type: "all", dateRange: { from: "", to: "" } });
    setDiscountsState((prev) => ({ ...prev, status: "all", activationType: "all", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const formatDiscountValue = (row: any) => {
    if (row.discountType === "percentage") {
      return `${row.discountValue ?? row.value ?? 0}%`;
    }
    return `$${parseFloat(row.discountValue ?? row.value ?? "0").toFixed(2)}`;
  };

  const setTableColumns = useMemo(() => [
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string) => <span className="text-[13px] text-[#505258]">{value || "-"}</span>,
    },
    {
      label: "Type", field: "discountType", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {value === "percentage" ? "Percentage" : value === "flat" ? "Flat" : value || "-"}
        </span>
      ),
    },
    {
      label: "Value", field: "discountValue", sortable: true,
      cellTemplate: (_value: any, row: any) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{formatDiscountValue(row)}</span>
      ),
    },
    {
      label: "Status", field: "active", sortable: true,
      cellTemplate: (value: any) => {
        const status = value ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Activation", field: "isAutoApply", sortable: true,
      cellTemplate: (value: any, row: any) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {value ? "Auto" : row.promoCode ? "Promo" : "Manual"}
        </span>
      ),
    },
    {
      label: "Promo Code", field: "promoCode", sortable: true,
      cellTemplate: (value: string | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
      ),
    },
    {
      label: "Scope", field: "appliesTo", sortable: false,
      cellTemplate: (value: any) => {
        const scopes = Array.isArray(value) ? value : ["order"];
        const labels: Record<string, string> = { order: "Orders", membership: "Memberships", entry_pass: "Passes" };
        return (
          <span className="text-[13px] text-[var(--secondary-text-color)]">
            {scopes.map((s: string) => labels[s] || s).join(", ")}
          </span>
        );
      },
    },
    {
      label: "Payment Methods", field: "includedPaymentMethods", sortable: false,
      cellTemplate: (value: any) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return <span className="text-[13px] text-[var(--secondary-text-color)]">All</span>;
        }
        const labels: Record<string, string> = { TOKEN: "SECH", CARD: "Card" };
        return (
          <span className="text-[13px] text-[var(--secondary-text-color)]">
            {value.map((v: string) => labels[v] || v).join(", ")}
          </span>
        );
      },
    },
    {
      label: "Usage Limit", field: "globalUsageLimit", sortable: true,
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? "Unlimited"}</span>,
    },
    {
      label: "Valid From", field: "startDate", sortable: true,
      cellTemplate: (value: string) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ? formatDate(value) : "-"}</span>,
    },
    {
      label: "Valid Until", field: "endDate", sortable: true,
      cellTemplate: (value: string) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ? formatDate(value) : "-"}</span>,
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/discounts/${r.id}/edit`) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredDiscounts = useMemo(() =>
    discounts.filter((discount: any) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      const status = discount.active ? "active" : "inactive";
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesType = activationTypeFilter === "all" || discount.discountType === activationTypeFilter;
      const matchesSearch = !search || discount.name?.toLowerCase().includes(search) || discount.promoCode?.toLowerCase().includes(search) || discount.discountType?.toLowerCase().includes(search);
      const fromDate = normalizeDate(discountsState.dateRange?.from);
      const toDate = normalizeDate(discountsState.dateRange?.to);
      const createdAt = new Date(discount.createdAt).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      return matchesStatus && matchesType && matchesSearch && matchesDateRange;
    }), [discounts, statusFilter, activationTypeFilter, debouncedSearch, discountsState.dateRange]
  );

  const sortedDiscounts = useMemo(() => {
    return [...filteredDiscounts].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a?.name ?? ""; bVal = b?.name ?? ""; break;
        case "discountType": aVal = a?.discountType ?? ""; bVal = b?.discountType ?? ""; break;
        case "discountValue": aVal = parseFloat(a?.discountValue ?? a?.value ?? "0"); bVal = parseFloat(b?.discountValue ?? b?.value ?? "0"); break;
        case "active": aVal = a?.active ? 1 : 0; bVal = b?.active ? 1 : 0; break;
        case "isAutoApply": aVal = a?.isAutoApply ? 1 : 0; bVal = b?.isAutoApply ? 1 : 0; break;
        case "promoCode": aVal = a?.promoCode ?? ""; bVal = b?.promoCode ?? ""; break;
        case "globalUsageLimit": aVal = a?.globalUsageLimit ?? 9999; bVal = b?.globalUsageLimit ?? 9999; break;
        case "startDate": aVal = a?.startDate ? new Date(a.startDate).getTime() : 0; bVal = b?.startDate ? new Date(b.startDate).getTime() : 0; break;
        case "endDate": aVal = a?.endDate ? new Date(a.endDate).getTime() : 0; bVal = b?.endDate ? new Date(b.endDate).getTime() : 0; break;
        default: aVal = a?.name ?? ""; bVal = b?.name ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredDiscounts, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedDiscounts.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedDiscounts = sortedDiscounts.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setDiscountsState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setDiscountsState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedDiscounts.length, handlePageChange, handleItemsPerPageChange };

  // Sub-route handling
  if (isAddRoute) {
    return <DiscountEditor onBack={() => navigate("/admin/discounts")} />;
  }

  if (isEditRoute) {
    return <DiscountEditor discountId={editParams?.id} onBack={() => navigate("/admin/discounts")} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Discounts</h2>
          <p className="text-gray-600 mb-4">Failed to load discounts</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Discounts</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setDiscountsState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => navigate("/admin/discounts/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Discount
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-0 h-[calc(100vh-130px)]">
          {!paginatedDiscounts.length && !isLoading ? (
            <NoRecordFound title="Discounts" description="There are no discounts available." action={
              <Button onClick={() => navigate("/admin/discounts/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add Discount
              </Button>
            } />
          ) : (
            <CommonGrid
              rowData={paginatedDiscounts}
              isLoading={isLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={discountFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete Discount" description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`} confirmText="Delete" variant="delete" />
    </div>
  );
}
